import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Grid,
  Chip,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { LocationProvider, PROVIDER_DELIVERY_MODE_OPTIONS } from '../types/provider';
import apiService from '../services/api';
import { resolveProviderLogoUrl } from '../utils/resolveProviderLogoUrl';
import EditProviderDialog from '../components/EditProviderDialog';
import ProviderAdminAccessCard from '../components/ProviderAdminAccessCard';
import ProviderTemporaryAccessManager from '../components/ProviderTemporaryAccessManager';
import { Avatar } from '@mui/material';

const EditProviderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState<LocationProvider | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchProvider(parseInt(id));
    }
  }, [id]);

  const fetchProvider = async (providerId: number) => {
    try {
      setLoading(true);
      setError('');
      const providerData = await apiService.getProvider(providerId);
      setProvider(providerData);
    } catch (error: any) {
      console.error('Erreur lors du chargement du partenaire:', error);
      setError(error.response?.data?.error || 'Erreur lors du chargement du partenaire');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = (updatedProvider: LocationProvider) => {
    setProvider(updatedProvider);
    setEditDialogOpen(false);
    void fetchProvider(updatedProvider.id);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'pending_approval': return 'warning';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'inactive': return 'Inactif';
      case 'pending_approval': return 'En attente';
      case 'suspended': return 'Suspendu';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/providers')}
        >
          Retour à la liste
        </Button>
      </Box>
    );
  }

  if (!provider) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Partenaire non trouvé
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/providers')}
        >
          Retour à la liste
        </Button>
      </Box>
    );
  }

  const logoSrc = resolveProviderLogoUrl(provider.logo_url, provider.logo);

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/providers')}
          sx={{ textDecoration: 'none' }}
        >
          Partenaires
        </Link>
        <Typography color="text.primary">{provider.name}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          {logoSrc ? (
            <Avatar
              src={logoSrc}
              sx={{ 
                width: 64, 
                height: 64,
                border: '2px solid',
                borderColor: 'primary.main',
              }}
              variant="rounded"
            />
          ) : (
            <BusinessIcon fontSize="large" color="primary" />
          )}
          <Box>
            <Typography variant="h4" component="h1">
              {provider.name}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <Chip
                label={getStatusLabel(provider.status)}
                color={getStatusColor(provider.status) as any}
                size="small"
              />
              <Chip
                label={provider.formatted_type}
                variant="outlined"
                size="small"
              />
            </Box>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setEditDialogOpen(true)}
        >
          Modifier
        </Button>
      </Box>

      {/* Informations du partenaire */}
      <Grid container spacing={3}>
        {/* Informations de base */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informations de base
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Nom
                </Typography>
                <Typography variant="body1">
                  {provider.name}
                </Typography>
              </Box>

              {provider.description && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {provider.description}
                  </Typography>
                </Box>
              )}

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Statut
                </Typography>
                <Chip
                  label={getStatusLabel(provider.status)}
                  color={getStatusColor(provider.status) as any}
                  size="small"
                />
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Type de partenaire
                </Typography>
                <Typography variant="body1">
                  {provider.formatted_type}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Date de création
                </Typography>
                <Typography variant="body1">
                  {new Date(provider.created_at).toLocaleDateString('fr-FR')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Contact */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informations de contact
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {provider.website && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Site web
                  </Typography>
                  <Typography variant="body1">
                    <Link href={provider.website} target="_blank" rel="noopener">
                      {provider.website}
                    </Link>
                  </Typography>
                </Box>
              )}

              {provider.phone && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Téléphone
                  </Typography>
                  <Typography variant="body1">
                    {provider.phone}
                  </Typography>
                </Box>
              )}

              {provider.email && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    <Link href={`mailto:${provider.email}`}>
                      {provider.email}
                    </Link>
                  </Typography>
                </Box>
              )}

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Administrateur principal
                </Typography>
                <Typography variant="body1">
                  {provider.admin_user?.email
                    ? `${provider.admin_user.first_name ?? ''} ${provider.admin_user.last_name ?? ''}`.trim() ||
                      provider.admin_user.email
                    : 'Aucun — créez un accès ci-dessous'}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Notifications e-mail (résumé)
                </Typography>
                <Typography variant="body1">
                  {provider.notification_emails_enabled === false
                    ? 'Désactivées'
                    : provider.notification_recipient_email
                      ? provider.notification_recipient_email
                      : `Repli sur le contact${provider.email ? ` (${provider.email})` : ''}${provider.admin_user?.email ? ` ou admin (${provider.admin_user.email})` : ''}`}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Visibilité publique
                </Typography>
                <Typography variant="body1">
                  {provider.visible_on_frontend === false
                    ? 'Masqué sur le frontend (menu, carousel, fiches publiques)'
                    : 'Affiché sur le frontend'}
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {provider.private_announcements
                    ? 'Annonces privées — catalogue masqué sur le site public'
                    : 'Catalogue public visible sur le site'}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Dépôt d&apos;annonce chez le partenaire
                </Typography>
                <Typography variant="body1">
                  {provider.settings?.encourage_partner_deposit_enabled === true
                    ? 'Activé — incitation au dépôt pour les annonces « objet trouvé »'
                    : 'Désactivé'}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Menus dashboard partenaire
                </Typography>
                <Typography variant="body1">
                  Mes matchings :{' '}
                  {provider.settings?.dashboard_menu_matchings_enabled === false ? 'masqué' : 'activé'}
                  {' · '}
                  Annonces liées (clients) :{' '}
                  {provider.settings?.dashboard_menu_linked_announcements_enabled === false
                    ? 'masqué'
                    : 'activé'}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Modes de livraison
                </Typography>
                <Typography variant="body1">
                  {PROVIDER_DELIVERY_MODE_OPTIONS.filter((option) => {
                    const modes = provider.delivery_modes ?? provider.settings?.delivery_modes;
                    if (!modes || typeof modes !== 'object') return true;
                    return modes[option.key] !== false;
                  })
                    .map((option) => option.label.replace(/^[^—]+—\s*/, ''))
                    .join(' · ') || 'Aucun mode activé'}
                </Typography>
              </Box>

              {provider.monthly_fee && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Frais mensuels
                  </Typography>
                  <Typography variant="body1">
                    {provider.monthly_fee} €
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <ProviderAdminAccessCard
            provider={provider}
            onProviderUpdated={(updated) => setProvider(updated)}
          />
        </Grid>

        <Grid item xs={12}>
          <ProviderTemporaryAccessManager
            providerId={provider.id}
            providerName={provider.name}
          />
        </Grid>

        {/* Adresse */}
        {provider.address && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Adresse
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="body1">
                  {provider.address.address && `${provider.address.address}, `}
                  {provider.address.postal_code && `${provider.address.postal_code} `}
                  {provider.address.city}
                  {provider.address.region && `, ${provider.address.region}`}
                  {provider.address.country && `, ${provider.address.country}`}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

      </Grid>

      {/* Actions */}
      <Box display="flex" gap={2} mt={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/providers')}
        >
          Retour à la liste
        </Button>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setEditDialogOpen(true)}
        >
          Modifier le partenaire
        </Button>
      </Box>

      {/* Dialog d'édition */}
      <EditProviderDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        provider={provider}
        onSave={handleSave}
        onProviderPatch={setProvider}
        onDeleted={() => {
          queryClient.invalidateQueries({ queryKey: ['providers'] });
          queryClient.invalidateQueries({ queryKey: ['provider-statistics'] });
          navigate('/providers');
        }}
      />
    </Box>
  );
};

export default EditProviderPage;
